import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import isBefore from 'date-fns/isBefore';
import isEqual from 'date-fns/isEqual';
import { motion } from 'framer-motion';
import { twMerge } from 'tailwind-merge';
import { faChevronRight } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import type { CollectButtonSource } from '@soundxyz/utils/analytics';
import { ContentLexicalStateV1 } from '@soundxyz/utils/lexical/ContentLexicalStateV1';
import { LexicalUtils } from '@components/Lexical/utils';
import { Timestamp } from '@components/basics/Timestamp';
import { Button } from '@components/main-components/button/Button';
import { PlayIndicatorBars } from '@components/main-components/cards/PlayIndicatorBars';
import { VerifiedTooltip } from '@components/main-components/tooltip/VerifiedTooltip';
import { type NotificationPostContentFragment } from '@graphql/reactQuery';
import { trackClickEvent } from '@lib/analytics';
import { SPACE } from '@utils/constants';

const MAX_CHAR_POST = 17;

export const NotificationWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <motion.div initial={{ opacity: 0, minHeight: 64 }} animate={{ opacity: 1, minHeight: 64 }}>
      <div className="group relative z-base flex min-h-[40px] w-full items-center justify-between gap-0.5 py-4 text-base-m after:absolute after:inset-0 after:-left-4 after:-right-4 after:z-below after:rounded-[6px]  after:content-[''] after:hover:bg-base50 md:min-h-[48px] md:border-b md:border-b-base200 lt:py-3 ">
        {children}
      </div>
    </motion.div>
  );
};

export const Indicator = ({ className }: { className?: string }) => (
  <div
    className={twMerge(
      'absolute inline-block h-2 w-2 rounded-full bg-fuchsia500 align-super',
      className,
    )}
  />
);

export const NotificationCollectButton = ({
  onClick,
  disabled,
  loading,
  releaseURI,
  releaseId,
  sourceComponent,
}: {
  onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  disabled?: boolean;
  loading?: boolean;
  releaseURI: string;
  releaseId: string;
  sourceComponent: CollectButtonSource;
}) => (
  <>
    {/* needs to be wrapped in a div because if we add an href the whole button gets wrapped in an a tag that render cause classname is not applied to the a tag */}
    <div className="flex lt:hidden">
      <Button
        label="Collect"
        iconOnly
        size="squared"
        className=" h-9 w-9 min-w-[36px] p-0 font-semibold [&_.loading-circle]:h-4 [&_.loading-circle]:w-4"
        loading={loading}
        disabled={disabled}
        href={releaseURI}
        variant="tertiary"
        leadingIcon={<FontAwesomeIcon icon={faChevronRight} />}
        onClick={() => {
          trackClickEvent({
            event: 'Collect Now Clicked',
            properties: {
              sourceComponent: sourceComponent,
              associationType: 'Release',
              associationId: releaseId,
            },
          });
        }}
      />
    </div>

    <div className="hidden lt:flex">
      <Button
        label="Collect"
        variant="tertiary"
        size="XS"
        className="h-7 min-w-[78px] flex-shrink-0 px-1 py-2 transition-all duration-500 ease-in-out hover:bg-white hover:shadow-tertiary "
        onClick={e => {
          trackClickEvent({
            event: 'Collect Now Clicked',
            properties: {
              sourceComponent: sourceComponent,
              associationType: 'Release',
              associationId: releaseId,
            },
          });

          onClick(e);
        }}
        disabled={disabled}
        loading={loading}
      />
    </div>
  </>
);

export const NotificationActionButton = ({
  label,
  icon,
  href,
  onClick,
  mobileHref,
  mobileOnClick,
}: {
  label: string;
  icon: React.ReactNode;
  href?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  /**
   * @note on mobile we use icons and sometimes we need to use a different href
   * use this
   */
  mobileHref?: string;
  /**
   * @note on mobile we use icons and sometimes we need to use a different onclick
   * use this
   */
  mobileOnClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}) => (
  <>
    <div className="flex lt:hidden">
      <Button
        label={label}
        iconOnly
        size="squared"
        className=" h-9 w-9 min-w-[36px] p-0 font-semibold [&_.loading-circle]:h-4 [&_.loading-circle]:w-4"
        variant="tertiary"
        leadingIcon={icon}
        href={mobileHref || href}
        onClick={mobileOnClick || onClick}
      />
    </div>
    <div className="hidden lt:flex">
      <Button
        label={label}
        variant="tertiary"
        size="XS"
        className=" h-7 min-w-[78px] flex-shrink-0 px-1 py-2 transition-all duration-500 ease-in-out hover:bg-white hover:shadow-tertiary"
        onClick={onClick}
        href={href}
      />
    </div>
  </>
);

export const MobileUsername = ({ username, isArtist }: { username: string; isArtist: boolean }) => (
  <div className="flex items-center dt:hidden">
    <span className="flex max-w-[130px] items-center overflow-hidden text-ellipsis whitespace-nowrap font-semibold">
      {username}
    </span>
    {isArtist && (
      <>
        {SPACE}
        <span className="block md:hidden">
          <VerifiedTooltip size={16} />
        </span>
        <span className="hidden md:block">
          <VerifiedTooltip size={18} />
        </span>
      </>
    )}
  </div>
);

export const NotificationInfo = ({
  timeStamp,
  hasRead,
}: {
  timeStamp: number;
  hasRead: boolean;
}) => (
  <div className="relative flex min-w-[40px] items-center justify-end gap-1.5 md:gap-2">
    <Timestamp timeStamp={timeStamp} className="!text-base-xs text-base400" />
    <div className="flex min-w-[8px] flex-shrink-0 items-center">
      {!hasRead && <Indicator className="relative" />}
    </div>
  </div>
);

export const NotificationLoadingSkeleton = ({
  length,
  avatar,
}: {
  length: number;
  avatar?: boolean;
}) => {
  return Array.from({ length }).map((_, i) => {
    const showAvatarLoader = (length > 1 && i % 4 === 0) || avatar;
    return (
      <div
        className="flex min-h-[64px] w-full items-center justify-between py-4 lt:py-3 "
        key={`NOTIFICATION-LOADING-STATE${i}`}
      >
        <div className="flex items-center gap-2">
          <div
            className={twMerge(
              'h-10 w-10 animate-pulse bg-base200',
              showAvatarLoader ? 'rounded-full' : 'rounded-md',
            )}
          />
          <div className="h-3.5 w-48 animate-pulse rounded-md bg-base200  lt:w-80" />
        </div>
        <div className="relative flex min-w-[40px] items-center justify-end">
          <div className="h-3.5 w-3.5 animate-pulse rounded-full bg-base200" />
        </div>
      </div>
    );
  });
};

export function hasReadNotification({
  timestamp,
  previousTimestamp,
}: {
  timestamp: number;
  previousTimestamp: number;
}) {
  return previousTimestamp
    ? isBefore(timestamp, previousTimestamp) || isEqual(timestamp, previousTimestamp)
    : false;
}

export const MotionPlayBars = ({}) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="mr-1 mt-1 hidden md:block"
    >
      <PlayIndicatorBars size="xs" />
    </motion.div>
  );
};

export const PlayingMotionLayoutShift = ({
  children,
  isPlaying,
  className,
}: {
  children: React.ReactNode;
  isPlaying: boolean;
  className?: string;
}) => {
  return (
    <motion.div
      layout="position"
      layoutDependency={isPlaying !== undefined ? isPlaying : false}
      transition={{ duration: 0.25 }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export function getContentSnapshot({
  content,
  maxLength = MAX_CHAR_POST,
}: {
  content: string;
  maxLength?: number;
}) {
  const isLong = content.length > maxLength;

  const wordsArray = isLong
    ? content.slice(0, maxLength).split(' ').slice(0, -1).concat('...')
    : content.split(' ');

  return wordsArray.map((word, i) => (
    <span key={`${word}-${i}`} className="text-base500">
      {i === 0 && '"'}
      {word}
      {i === wordsArray.length - 1 && '"'}
      {i !== wordsArray.length - 1 && SPACE}
    </span>
  ));
}

const MAX_RELEASE_TITLE = 20;

export function getReleaseTitleSnapshot(releaseTitle: string) {
  const isLong = releaseTitle.length > MAX_RELEASE_TITLE;

  const wordsArray = isLong
    ? releaseTitle.slice(0, MAX_RELEASE_TITLE).split(' ').slice(0, -1).concat('...')
    : releaseTitle.split(' ');

  return wordsArray.map((word, i) => (
    <span key={`${word}-${i}`}>
      {word}
      {i !== wordsArray.length - 1 && SPACE}
    </span>
  ));
}

export function getCollectedReleasesPreview({
  collectedReleases,
  maxReleases,
}: {
  collectedReleases: {
    release: {
      id: string;
      title: string;
      artist: { name: string };
    };
  }[];
  maxReleases: number;
}) {
  return collectedReleases.slice(0, maxReleases).map(
    (
      {
        release: {
          id,
          title,
          artist: { name },
        },
      },
      index,
    ) => {
      const isFirst = index === 0;
      const isLast = index === Math.min(collectedReleases.length, maxReleases) - 1;

      return (
        <span key={id}>
          {collectedReleases.length === 2 && isLast ? (
            <>
              {SPACE}and{SPACE}
            </>
          ) : collectedReleases.length > 2 && !isFirst && isLast ? (
            <>, and{SPACE}</>
          ) : !isFirst ? (
            <>,{SPACE}</>
          ) : (
            <></>
          )}
          <span className="font-semibold">
            {title} - {name}
          </span>
        </span>
      );
    },
  );
}

export function contentForPost({
  postContentFrag,
  includeYourPrefix,
}: {
  postContentFrag: NotificationPostContentFragment;
  includeYourPrefix: boolean;
}) {
  if (!postContentFrag) {
    return null;
  }

  const parsedLexicalContent = ContentLexicalStateV1.safeParse(postContentFrag.lexicalContent);
  const lexicalContent = LexicalUtils.generateTextForNode(
    parsedLexicalContent.success ? parsedLexicalContent.data.root : undefined,
  );
  const lexicalContentOrNormalContent = lexicalContent.length
    ? lexicalContent
    : postContentFrag.content;

  if (lexicalContentOrNormalContent) {
    const content = getContentSnapshot({
      content: lexicalContentOrNormalContent,
    });

    return includeYourPrefix ? (
      <>
        <span>your post</span>
        {SPACE}
        {content}
      </>
    ) : (
      content
    );
  }

  if (postContentFrag.__typename === 'ActivityFeedGroupBackingPost') {
    switch (postContentFrag.activityFeedGroup.information.__typename) {
      case 'ReleaseDroppedAggregate': {
        const {
          activityFeedGroup: {
            information: {
              release: {
                title,
                artist: { name: artistName },
              },
            },
          },
        } = postContentFrag;

        const content = getContentSnapshot({ content: `${title} - ${artistName}`, maxLength: 30 });

        return includeYourPrefix ? (
          <>
            <span>your drop activity</span>
            {SPACE}
            {content}
          </>
        ) : (
          content
        );
      }
      case 'UserCollectedManySongsAggregate': {
        const {
          activityFeedGroup: {
            information: { collectedReleases },
          },
        } = postContentFrag;

        const releasesFormatted = collectedReleases
          .slice(0, 3)
          .map(
            ({
              release: {
                title,
                artist: { name: artistName },
              },
            }) => `${title} - ${artistName}`,
          )
          .join(', ');

        const content = getContentSnapshot({ content: releasesFormatted, maxLength: 24 });

        return includeYourPrefix ? (
          <>
            <span>your collecting activity</span>
            {SPACE}
            {content}
          </>
        ) : (
          content
        );
      }
      default:
        return 'your activity';
    }
  }

  return null;
}
